import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import unitService from '../../services/unitService';
import '../../styles/ImsTheme.css';
import './ProductCategoryEditPage.css';
import { parseApiError } from '../../utils/errorUtils';
import { ArrowLeft, Save } from 'lucide-react';

// Max length constants for validation
const MAX_LENGTHS = {
  unitName: 30,
  unitShortName: 10,
  description: 200,
};

interface FieldErrors {
  unitName?: string;
  unitShortName?: string;
  description?: string;
}

interface UnitForm {
  unitName: string;
  unitShortName: string;
  description: string;
  unitCategory: string;
  decimalAllowed: boolean;
  isBaseUnit: boolean;
}

const UnitCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formData, setFormData] = useState<UnitForm>({
    unitName: '',
    unitShortName: '',
    description: '',
    unitCategory: '',
    decimalAllowed: false,
    isBaseUnit: true,
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';

    let finalValue: any;
    if (isCheckbox) {
      finalValue = (e.target as HTMLInputElement).checked;
    } else {
      finalValue = value;
      if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
        finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
      }
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    if (!isCheckbox) {
      // Clear field error when user starts typing
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // Validate Unit Name
    if (!formData.unitName.trim()) {
      errors.unitName = t('validation.unitNameRequired');
      isValid = false;
    } else if (formData.unitName.length > MAX_LENGTHS.unitName) {
      errors.unitName = t('unit.formValidation.nameMaxLength');
      isValid = false;
    }

    // Validate Abbreviation
    if (!formData.unitShortName.trim()) {
      errors.unitShortName = t('validation.unitShortNameRequired');
      isValid = false;
    } else if (formData.unitShortName.length > MAX_LENGTHS.unitShortName) {
      errors.unitShortName = t('unit.formValidation.shortNameMaxLength');
      isValid = false;
    }

    // Validate Description (optional — max length only)
    if (formData.description && formData.description.length > MAX_LENGTHS.description) {
      errors.description = t('unit.formValidation.descriptionMaxLength');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await unitService.createUnit(formData);
      navigate('/products/units', {
        state: { message: t('unit.createSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('unit.failedToCreateUnit')).message);
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="edit-container">
        {/* Navigation Breadcrumb */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/products/units')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('unit.newUnit')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/products/units')} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={loading}>
              {loading ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          {/* Form Section */}
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('unit.createNewUnit')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit}>
                  {/* Unit Name */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unit.unitName')} <span className="required">*</span>
                      <span className="char-counter">{formData.unitName.length}/{MAX_LENGTHS.unitName}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.unitName ? 'is-invalid' : ''}`}
                      name="unitName"
                      value={formData.unitName}
                      onChange={handleInputChange}
                      placeholder={t('unit.unitName')}
                      maxLength={MAX_LENGTHS.unitName}
                    />
                    {fieldErrors.unitName && <small className="form-error">{fieldErrors.unitName}</small>}
                    {!fieldErrors.unitName && <small className="form-helper">e.g., Kilogram, Meter, Liter</small>}
                  </div>

                  {/* Abbreviation */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unit.abbreviation')} <span className="required">*</span>
                      <span className="char-counter">{formData.unitShortName.length}/{MAX_LENGTHS.unitShortName}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.unitShortName ? 'is-invalid' : ''}`}
                      name="unitShortName"
                      value={formData.unitShortName}
                      onChange={handleInputChange}
                      placeholder="e.g., kg, m, L"
                      maxLength={MAX_LENGTHS.unitShortName}
                    />
                    {fieldErrors.unitShortName && <small className="form-error">{fieldErrors.unitShortName}</small>}
                    {!fieldErrors.unitShortName && <small className="form-helper">Short form of unit (max 10 characters)</small>}
                  </div>

                  {/* Unit Category */}
                  <div className="form-group">
                    <label className="form-label">{t('unit.unitCategory')}</label>
                    <select
                      className="form-input"
                      name="unitCategory"
                      value={formData.unitCategory}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('unit.selectCategory')}</option>
                      <option value="Weight">{t('unit.categories.weight')}</option>
                      <option value="Volume">{t('unit.categories.volume')}</option>
                      <option value="Length">{t('unit.categories.length')}</option>
                      <option value="Area">{t('unit.categories.area')}</option>
                      <option value="Quantity">{t('unit.categories.quantity')}</option>
                      <option value="Time">{t('unit.categories.time')}</option>
                      <option value="Temperature">{t('unit.categories.temperature')}</option>
                      <option value="Other">{t('unit.categories.other')}</option>
                    </select>
                    <small className="form-helper">Category of unit (optional)</small>
                  </div>

                  {/* Description */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unit.description')}
                      <span className="char-counter">{formData.description.length}/{MAX_LENGTHS.description}</span>
                    </label>
                    <textarea
                      className={`form-textarea ${fieldErrors.description ? 'is-invalid' : ''}`}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder={t('unit.description')}
                      rows={4}
                      maxLength={MAX_LENGTHS.description}
                    />
                    {fieldErrors.description && <small className="form-error">{fieldErrors.description}</small>}
                    {!fieldErrors.description && <small className="form-helper">Optional: describe the unit of measure</small>}
                  </div>

                  {/* Decimal Allowed */}
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="decimalAllowed"
                        checked={formData.decimalAllowed}
                        onChange={handleInputChange}
                      />
                      <span>{t('unit.decimalAllowed')}</span>
                    </label>
                  </div>

                  {/* Is Base Unit */}
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="isBaseUnit"
                        checked={formData.isBaseUnit}
                        onChange={handleInputChange}
                      />
                      <span>{t('unit.isBaseUnit')}</span>
                    </label>
                  </div>

                </form>
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.information')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="help-item">
                  <strong>{t('unit.unitCode')}</strong>
                  <p>Auto-generated by the system upon saving</p>
                </div>
                <div className="help-item">
                  <strong>{t('unit.unitName')}</strong>
                  <p>Full name of the unit (e.g., Kilogram, Meter)</p>
                </div>
                <div className="help-item">
                  <strong>{t('unit.abbreviation')}</strong>
                  <p>Short form used in documents and forms (max 10 characters)</p>
                </div>
                <div className="help-item">
                  <strong>{t('unit.unitCategory')}</strong>
                  <p>Group this unit belongs to (e.g., Weight, Volume)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UnitCreatePage;
